import { FormSearchInput } from "@/components/Form/FormSearchInput";
import { API_CONSTANT_MAP } from "@/services/paths";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { PagamentoTableType, PagamentoType } from "./types";
import { getPagamentoById } from "@/services/pagamento";
import { Button, Col, DatePicker, DatePickerProps, Divider, Form, Input, Popconfirm, Row, Table, TableColumnsType, Tag, Typography } from "antd";
import { Container } from "./styles";
import { useParams } from "react-router-dom";
import ChequeModal from "@/components/chequeModal";
import dayjs, { Dayjs } from 'dayjs';
import moment from "moment";
import { ehParcelaTrocada, somarParcelasVigentes } from "@/utils/totaisMatricula";
import ComprovanteUpload from "@/components/ComprovanteUpload";
import { ComprovantePayload } from "@/utils/comprovante";

function formatCurrency(value: string | number) {
  if (value === null || value === undefined || value === "") return "";
  const number = typeof value === "number" ? value : Number(value.toString().replace(/\D/g, "")) / 100;
  return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseCurrency(value: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d]/g, "")) / 100;
}

const dateFormat = 'DD/MM/YYYY';

const parseFlexibleDate = (value: unknown) => {
  if (!value) {
    return moment.invalid();
  }

  const parsed = moment(value as any, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed : moment(value as any);
};

const formatDateToPtBr = (value: unknown) => {
  const parsed = parseFlexibleDate(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

const { Title } = Typography;

type Props = {
  current?: number;
  disabled?: boolean;
  /**
   * Total que as parcelas precisam cobrir. Na matrícula é disciplinas + produtos;
   * na venda avulsa é o total dos produtos vendidos.
   */
  valorTotal: number;
  /** Rótulo do total exibido no topo do bloco. */
  rotuloValorTotal?: string;
  /** Nome sugerido pelo botão de cópia do modal de cheques. */
  nomeParaCopiarCheque?: string;
  /** Rótulo do botão de cópia do modal de cheques. */
  rotuloCopiarCheque?: string;
};

/**
 * Bloco de pagamento em parcelas: escolha da forma de pagamento, valor, número de
 * parcelas, data de início, CV do cartão e os dados dos cheques. Lê e grava os
 * campos `pagamentos` e `cheques` do formulário em contexto — é usado tanto pela
 * etapa Pagamento da matrícula quanto pela venda avulsa de produtos.
 */
function PagamentoParcelas({
  disabled = false,
  valorTotal,
  rotuloValorTotal = 'Valor Total',
  nomeParaCopiarCheque,
  rotuloCopiarCheque,
}: Props) {

  const {
    getValues,
    watch,
    setValue,
    register,
    control,
  } = useFormContext<any>();

  const { id } = useParams();
  const isEditable = id ? true : false;
  const dataInicioDefault = moment().format(dateFormat);

  const [pagamentoType, setPagamentoType] = useState<PagamentoType>();
  const valorTotalDisciplinas = valorTotal;
  const [valorPendente, setValorPendente] = useState<number>(0);
  const [valorPagamento, setValorPagamento] = useState<number>(0);
  const [selecaoParcelas, setSelecaoParcelas] = useState<any[]>([]);
  const [tipoPagamentoSelecionado, setTipoPagamentoSelecionado] = useState<boolean>(false);
  const [pagamentoParcelado, setPagamentoParcelado] = useState<boolean>(false);
  const [lancadoTotal, setLancadoTotal] = useState<boolean>(false);
  const pagamentos = watch('pagamentos');
  const pagamentoId = watch('pagamentoId');
  const [dataInicioValue, setDataInicioValue] = useState<string | null>(dataInicioDefault);
  const [cvCartao, setCvCartao] = useState<number>(0);
  const [openedChequeModal, setOpenedChequeModal] = useState<boolean>(false);
  const [shouldClearCheques, setShouldClearCheques] = useState<boolean>(false);
  const [chequeToDelete, setChequeToDelete] = useState<number | null>(null);

  const {
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'pagamentos',
  });

  // useEffect para buscar nomes dos pagamentos quando editando uma matrícula
  useEffect(() => {
    const carregarNomesPagamentos = async () => {
      if (isEditable && pagamentos && pagamentos.length > 0) {
        const pagamentosAtualizados = await Promise.all(
          pagamentos.map(async (pagamento: any) => {
            if (pagamento.pagamentoId && !pagamento.tipoPagamento) {
              try {
                const { data: pagamentoData } = await getPagamentoById(pagamento.pagamentoId.toString());
                return {
                  ...pagamento,
                  tipoPagamento: pagamentoData?.tipoPagamento || 'N/A',
                  nomePagamento: pagamentoData?.nome || 'N/A',
                };
              } catch (error) {
                console.error('Erro ao buscar pagamento:', error);
                return {
                  ...pagamento,
                  tipoPagamento: 'N/A',
                  nomePagamento: 'N/A',
                };
              }
            }
            return pagamento;
          })
        );
        
        // Atualizar apenas se houve mudanças
        const houveMudancas = pagamentosAtualizados.some((pag, index) => 
          pag.tipoPagamento !== pagamentos[index]?.tipoPagamento ||
          pag.nomePagamento !== pagamentos[index]?.nomePagamento
        );
        
        if (houveMudancas) {
          setValue('pagamentos', pagamentosAtualizados);
        }
      }
    };

    carregarNomesPagamentos();
  }, [isEditable, pagamentos?.length, setValue]); // Dependências específicas para evitar loops

  const getTipoPagamento = async (e: string) => {
    const pagamentoId = e;
    if (!pagamentoId) {
      return;
    }
    const data: any = await getPagamentoById(pagamentoId);

    setPagamentoType(data?.data);

    let parcelaAdd = [];
    for (let index = 0; index < data?.data.quantidadeParcelas; index++) {
      if (data?.data.tipoPagamento === "Dinheiro") {
        parcelaAdd.push({ label: 'À Vista', value: 1 });
        // Automaticamente selecionar "À Vista" para dinheiro
        setValue("parcelasQtd", 1);
        break;
      }

      parcelaAdd.push({ label: `${index + 1}x`, value: index + 1 });
      setPagamentoParcelado(true);
    }
    setSelecaoParcelas(parcelaAdd);
    setTipoPagamentoSelecionado(true);
  };

  const removerParcelas = () => {
    const pagamentosAtivos = getValues('pagamentos') || [];
    const temCheques = pagamentosAtivos.some((pagamento: any) => pagamento.tipoPagamento === "Cheque");

    // As trocadas ficam: são registro de troca, não parcela lançada aqui.
    setValue('pagamentos', pagamentosAtivos.filter(ehParcelaTrocada));
    // Redefine o valor de pagamento com o valor total das disciplinas
    setValorPagamento(valorTotalDisciplinas);
    setValue("valorPagamento", formatCurrency(valorTotalDisciplinas));
    setValorPendente(valorTotalDisciplinas);
    setLancadoTotal(false);
    
    // Se havia cheques, sinaliza para limpar os dados do modal
    if (temCheques) {
      setShouldClearCheques(true);
      // Reset o estado após um tempo para permitir futuras limpezas
      setTimeout(() => setShouldClearCheques(false), 100);
    }
  };

  const onChangeDate: DatePickerProps<Dayjs[]>['onChange'] = (date, dateString) => {
    console.log(date, dateString);
  };

  const addParcelas = async () => {
    const parcela = getValues('parcelasQtd');
    let pagamentos = getValues('pagamentos');
    let valorRestante = 0;
    let totalParcelas = somarParcelasVigentes(pagamentos);

    // Usar o valor numérico já convertido do state
    const valorPagamentoNumerico = valorPagamento;
    let totalTipoPagamento = 0;

    for (let index = 0; index < parcela; index++) {
      const dataBase = parseFlexibleDate(dataInicioValue);
      const dataParcela = (dataBase.isValid() ? dataBase : moment()).clone().add(index, 'month');

      if (index === parcela - 1) {
        valorRestante = Number(valorPagamentoNumerico) - totalTipoPagamento;
      }
      let valorParcela = Number((Number(valorPagamentoNumerico) / parcela).toFixed(2));
      append({
        parcela: pagamentos.length + index + 1, // Usar índice sequencial baseado na lista atual
        pagamentoId: pagamentoType?.id ?? 0,
        tipoPagamento: pagamentoType?.tipoPagamento ?? "",
        valor: valorRestante > 0 ? valorRestante : valorParcela,
        dataParcela: dataParcela.format('DD/MM/YYYY'),
        cvCartao: cvCartao,
        pagamento: new Object(pagamentoType),
        nomePagamento: pagamentoType?.nome ?? "",
      });
      totalParcelas += valorParcela;
      totalTipoPagamento += valorParcela;
    }
    let valorRestanteTotal = valorTotalDisciplinas - totalParcelas;
    setValorPendente(valorRestanteTotal);
    setLancadoTotal(valorRestanteTotal === 0);
    
    if (pagamentoType?.tipoPagamento === "Cheque") {
      setOpenedChequeModal(true);
    }
    
    // Limpar os campos após adicionar
    setValue("pagamentoId", "");
    setValue("parcelasQtd", "");
    setValue("dataInicio", "");
    //setDataInicioValue(dataInicioDefault);
    setCvCartao(0);
    setPagamentoType(undefined);
    setSelecaoParcelas([]);
    setTipoPagamentoSelecionado(false);
    setPagamentoParcelado(false);
    
    // Preencher campo Valor Pagamento com valor pendente (se houver)
    if (valorRestanteTotal > 0) {
      setValorPagamento(valorRestanteTotal);
      setValue("valorPagamento", formatCurrency(valorRestanteTotal));
    } else {
      setValorPagamento(0);
      setValue("valorPagamento", "");
    }
  }

  /**
   * Mesma regra da API (PagamentoPixHelper): não existe TipoPagamento
   * "Transferência", ela é uma forma de pagamento com "transfer" no nome.
   */
  const ehPixOuTransferencia = (record: any) => {
    const tipo = record?.tipoPagamento ?? record?.pagamento?.tipoPagamento;
    const nome = record?.nomePagamento ?? record?.pagamento?.nome ?? "";
    return tipo === "Pix" || String(nome).toLowerCase().includes("transfer");
  };

  /** Guarda o arquivo escolhido na parcela; ele sobe junto no Salvar Cadastro. */
  const definirComprovante = (parcela: number, comprovante: ComprovantePayload | null) => {
    const atuais = getValues("pagamentos") ?? [];
    setValue(
      "pagamentos",
      atuais.map((item: any) =>
        item?.parcela === parcela ? { ...item, comprovante } : item
      )
    );
  };

  const getCvCartaoOrCheque = (record: any, rowIndex: number) => {
    const tipoPagamento = record?.tipoPagamento ?? record?.pagamento?.tipoPagamento;

    if (tipoPagamento === 'Cartão') {
      return record?.cvCartao ?? '-';
    }

    if (tipoPagamento === 'Cheque') {
      const pagamentosAtuais = pagamentos ?? [];
      const cheques = getValues('cheques') ?? [];

      const indiceLinha = rowIndex >= 0
        ? rowIndex
        : pagamentosAtuais.findIndex((pagamento: any) => pagamento?.parcela === record?.parcela);

      if (indiceLinha < 0) {
        return '-';
      }

      // Mapeia por ordem de ocorrência de pagamentos do tipo cheque.
      const indiceCheque = pagamentosAtuais
        .slice(0, indiceLinha + 1)
        .filter((pagamento: any) => (pagamento?.tipoPagamento ?? pagamento?.pagamento?.tipoPagamento) === 'Cheque')
        .length - 1;

      return cheques?.[indiceCheque]?.numero ?? '-';
    }

    return record?.cvCartao ?? '-';
  }

  useEffect(() => {
    const pagamentosAtuais = getValues('pagamentos');
    if (!pagamentosAtuais || pagamentosAtuais.length === 0) {
      getTipoPagamento(pagamentoId);
      setValorPagamento(valorTotalDisciplinas);
      // Definir o valor formatado no campo
      setValue("valorPagamento", formatCurrency(valorTotalDisciplinas));
    }
  }, [pagamentoId]);

  // useEffect para recalcular valor pendente quando pagamentos mudarem
  useEffect(() => {
    if (valorTotalDisciplinas > 0 && pagamentos) {
      // Parcela trocada por outra forma de pagamento não é pagamento: contá-la
      // deixaria o valor pendente negativo depois de uma troca.
      const totalPago = somarParcelasVigentes(pagamentos);
      const pendente = valorTotalDisciplinas - totalPago;
      setValorPendente(pendente);
      setLancadoTotal(pendente === 0);
      
      // Se há valor pendente, preencher automaticamente o campo valor pagamento
      if (pendente > 0) {
        setValorPagamento(pendente);
        setValue("valorPagamento", formatCurrency(pendente));
      } else {
        // Se não há valor pendente, limpar o campo
        setValorPagamento(0);
        setValue("valorPagamento", "");
      }
    }
  }, [pagamentos, valorTotalDisciplinas]);

  const handleDelete = (key: React.Key) => {
    const pagamentoParaExcluir = pagamentos.find((item: any) => item.parcela === key);
    const newData = pagamentos.filter((item: any) => item.parcela !== key);
    setValue('pagamentos', newData);
    let valorRestante = valorTotalDisciplinas - somarParcelasVigentes(newData);
    setValorPendente(valorRestante);
    
    // Se o pagamento excluído é do tipo cheque, excluir o cheque correspondente
    if (pagamentoParaExcluir && pagamentoParaExcluir.tipoPagamento === "Cheque") {
      setChequeToDelete(Number(key));
      // Reset o estado após um tempo para permitir futuras exclusões
      setTimeout(() => setChequeToDelete(null), 100);
    }
  };

  const setValorPagamentoValue = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length < 3) value = value.padStart(3, "0");
    const number = Number(value) / 100;
    const formattedValue = number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    setValue("valorPagamento", formattedValue);
    setValorPagamento(number);
  };

  const columns: TableColumnsType<PagamentoTableType> = [
    {
      title: 'Nr. Parcela',
      dataIndex: 'parcela',
      render: (text: string) => <a>{text}</a>,
    },
    {
      title: 'Pagamento',
      dataIndex: 'nomePagamento',
      render: (text: string) => <a>{text}</a>,
    },
    {
      title: 'Data',
      dataIndex: 'dataParcela',
      render: (text: string) => {
        const formatted = formatDateToPtBr(text);
        return <a>{formatted || text}</a>;
      },
    },
    {
      key: 'valor',
      title: 'Valor',
      dataIndex: 'valor',
      render: (_, { valor }) => { return valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) },
    },
    {
      title: 'CV Cartão / Cheque',
      dataIndex: 'cvCartao',
      render: (_: string, record: any, index: number) => <a>{getCvCartaoOrCheque(record, index)}</a>,
    },
    {
      title: 'Comprovante',
      dataIndex: 'comprovante',
      render: (_: unknown, record: any) => {
        // Só PIX/Transferência tem comprovante; anexar nunca é obrigatório.
        if (!ehPixOuTransferencia(record)) return '-';

        if (ehParcelaTrocada(record)) return '-';

        const jaGravados = record?.quantidadeComprovantes ?? 0;

        return (
          <>
            {jaGravados > 0 && (
              <div className="text-xs opacity-70 mb-1">
                {jaGravados} já anexado(s)
              </div>
            )}
            <ComprovanteUpload
              size="small"
              mostrarAviso={false}
              disabled={disabled}
              comprovante={record?.comprovante ?? null}
              onChange={(comprovante) => definirComprovante(record.parcela, comprovante)}
              rotulo={jaGravados > 0 ? 'Anexar outro' : 'Anexar'}
            />
          </>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_: string, record: any) => {
        if (ehParcelaTrocada(record)) {
          return (
            <Tag color="purple">
              {record.trocaPagamentoNome ? `Trocado — ${record.trocaPagamentoNome}` : 'Trocado'}
            </Tag>
          );
        }
        // Parcela ainda não salva (lançada agora na tela) não tem status.
        if (!record?.status) return '-';
        return <Tag color={record.status === 'Pago' ? 'green' : 'default'}>{record.status}</Tag>;
      },
    },
    {
      title: 'Ação',
      dataIndex: 'acao',
      render: (_, record) => {
        // A parcela trocada é histórico: quem desfaz a troca é o controle
        // financeiro, não a tela de matrícula.
        if (ehParcelaTrocada(record)) return null;

        return pagamentos.length >= 1 ? (
          disabled ? (
            <Typography.Link disabled>Delete</Typography.Link>
          ) : (
            <Popconfirm title="Confirma?" onConfirm={() => handleDelete(record.parcela)}>
              <a>Delete</a>
            </Popconfirm>
          )
        ) : null;
      },
    },
  ];

  const handleCloseModal = () => {
    setOpenedChequeModal(false);
  };

  return (
    <>
      <div className="mb-8">
        <Container>
        <Form>
            <div className="mt-8">
              <Row gutter={[16, 24]}>
                <Col md={24} lg={8}>
                  <Title level={3}>{rotuloValorTotal}: {valorTotalDisciplinas.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Title>
                </Col>
                <Col md={24} lg={8} hidden={!valorPendente || valorPendente === 0}>
                  <Title level={3}>Valor Pendente: {valorPendente.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Title>
                </Col>
              </Row>
              <Row gutter={[16, 24]}>
                <Col md={24} lg={8}>
                  <FormSearchInput
                    id="tipoPagamento"
                    name="pagamentoId"
                    property="nome"
                    optionUrl={API_CONSTANT_MAP.pagamento.concat("?Ativo=true")}
                    inputValueName="nome"
                    valueProperty="id"
                    label="Tipo de Pagamento"
                    onChangeCallback={getTipoPagamento}
                    disabled={disabled || lancadoTotal}
                  />
                </Col>
              </Row>
              <Row gutter={[16, 24]}>
                <Col md={24} lg={8}>
                  <label>Valor Pagamento</label>
                  <Input
                    type="text"
                    placeholder="R$ 0,00"
                    {...register("valorPagamento", { required: "Valor é obrigatório" })}
                    onChange={setValorPagamentoValue}
                    value={watch("valorPagamento")}
                    disabled={disabled}
                  />
                </Col>
              </Row>
              <Row gutter={[16, 24]} hidden={!tipoPagamentoSelecionado}>
                <Col md={24} lg={8}>
                  <FormSearchInput
                    id="qtdParcelas"
                    name="parcelasQtd"
                    property="label"
                    optionStatic={selecaoParcelas}
                    inputValueName="label"
                    valueProperty="value"
                    label="Parcelas"
                    disabled={disabled || pagamentoType?.tipoPagamento === "Dinheiro"}
                  />
                </Col>
                <Col md={24} lg={8}>
                  <Form.Item
                    label="Data Início"
                    name="dataInicio"
                    labelCol={{ span: 24 }} // Define a largura do label
                    wrapperCol={{ span: 24 }} // Define a largura do campo de entrada
                    style={{ marginTop: '-6px' }}
                  >
                    <DatePicker
                      defaultValue={dayjs(moment().format('DD/MM/YYYY'), dateFormat)} 
                      value={(() => {
                        const parsed = parseFlexibleDate(dataInicioValue);
                        return parsed.isValid() ? dayjs(parsed.format('YYYY-MM-DD')) : null;
                      })()}
                      onChange={(date, dateString) => setDataInicioValue(Array.isArray(dateString) ? dateString[0] : dateString)}
                      format="DD/MM/YYYY"
                      disabled={disabled || pagamentoType?.tipoPagamento === "Dinheiro"}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 24]} hidden={!tipoPagamentoSelecionado || pagamentoType?.tipoPagamento != "Cartão"}>
                <Col md={24} lg={8}>
                  <label>CV Cartão</label>
                  <Input
                    type="number"
                    onChange={(e) => setCvCartao(Number(e.target.value))}
                    disabled={disabled}
                  />
                </Col>
              </Row>
            </div>
          </Form>
          <Row gutter={[16, 24]} hidden={!tipoPagamentoSelecionado && (!pagamentos || pagamentos.length === 0)} style={{ marginTop: '10px' }}>
            <Col>
              <Button type="primary" ghost onClick={addParcelas} disabled={disabled}>Adicionar</Button>
            </Col>
            <Col>
              <Button type="primary" ghost onClick={removerParcelas} disabled={disabled}>Remover Tudo</Button>
            </Col>
            {pagamentos && pagamentos.some((pagamento: any) => pagamento.tipoPagamento === "Cheque") && (
              // Mesmo com a etapa bloqueada (não-Master), a consulta dos dados
              // dos cheques fica liberada — o modal abre em modo somente leitura.
              <Col>
                <Button type="primary" ghost onClick={() => setOpenedChequeModal(true)}>Cheques</Button>
              </Col>
            )}
          </Row>
          <Divider />
          <Row gutter={[16, 24]} hidden={!isEditable && (!pagamentos || pagamentos.length === 0)}>
            <Col md={24} lg={24}>
              <Table
                columns={columns}
                dataSource={pagamentos}
                pagination={false}
              />
            </Col>
          </Row>
          <ChequeModal
            parcelas={pagamentos ? pagamentos.filter((p: any) => p.tipoPagamento === "Cheque").length : 0}
            opened={openedChequeModal}
            onClose={handleCloseModal}
            shouldClearCheques={shouldClearCheques}
            chequeToDelete={chequeToDelete}
            readOnly={disabled}
            nomeParaCopiar={nomeParaCopiarCheque}
            rotuloCopiar={rotuloCopiarCheque}
          />
        </Container>
      </div >
    </>
  )
}

export default PagamentoParcelas;
